/**
 * STK-352 — recap detail button table.
 *
 * Pure mapping from the classified action state (`state.ts`) to button
 * descriptors. This file is the **literal encoding of the issue's 10 cases**
 * plus the export-phase overlays (confirming / failed / ill), the EXPORTED /
 * cancelled lifecycles, and the `recap_modify_mode`-OFF confirmed variant.
 *
 * Each branch is standalone — it never references another case ("see case N").
 * Descriptors carry no JSX and no behavior: just a stable `id`, an i18n
 * `labelKey` (+ fallback), a `tone`, and the name of the `transition` to run.
 * `RecapActionBar` renders them and wires each `transition` to the matching
 * command in `useRecapTransitions`. To pilot a single transition by hand, wire
 * a button straight to that command; to revert, restore the old call site.
 *
 * Precedence (mirrors the legacy `getButtonState` priority chain so behavior
 * doesn't change): cancelled → proposal(delegated) → confirming → (outside
 * modify mode) failed → ill → exported → the 10 live cases. Inside modify mode
 * the user is actively editing, so failed/ill overlays defer until they save.
 */
import { PreOrderStatus } from "@/app/api/types/preOrder";
import type { RecapActionState } from "./state";

export type RecapTransitionId =
  // Status-change actions (rendered in the order/hero header): persist + accept
  // AI + trigger ERP, reject AI (a review-state change), unconfirm, delete, …
  | "acceptAndConfirm"
  | "acceptAiPending"
  | "rejectAi"
  | "unconfirm"
  | "deleteOrder"
  | "retryExport"
  | "healIll"
  | "restore"
  // Local-modification actions (rendered in the table header): no status change.
  | "saveModifications"
  | "cancelModifications"
  // Enter modify mode on a CONFIRMED row (flag ON) so its cells unlock for the
  // modify-confirmed re-export flow. No status change on its own.
  | "enterModify";

export type ButtonTone =
  | "primary" // orange — "Valider et exporter" (validate mods + export)
  | "confirm" // blue — plain "Exporter" on a pending order (no mods)
  | "validate" // orange — table-header "Valider les modifications" (save local mods, no ERP)
  | "cancel" // red — table-header "Annuler les modifications"
  | "ai" // violet AI accept (filled)
  | "ai-outline" // violet AI reject/decline (outline)
  | "destructive" // red delete / reject
  | "neutral" // unconfirm
  | "confirmed" // blue static pill
  | "exported" // green static pill
  | "confirming" // gray spinner pill
  | "failed" // red failed pill (retry)
  | "ill"; // amber drift pill (heal)

export interface ButtonSpec {
  /** Stable, locale-independent id → `data-action`. */
  id: string;
  /**
   * `data-testid`. Defaults (in `ActionButton`) to `recap-action-<id>`, but we
   * pin the legacy testids the existing E2E suite resolves so the renderer
   * swap doesn't break specs (`recap-ai-accept`, `recap-focus-delete`, …).
   */
  testId?: string;
  /** Legacy `data-state` (locale-independent selector for the export pills). */
  dataState?: string;
  labelKey: string;
  fallbackLabel: string;
  tone: ButtonTone;
  /** Absent → a static indicator pill (confirmed / exported / confirming). */
  transition?: RecapTransitionId;
  /** Structural disable (e.g. the flag-OFF "Confirm" pill on a confirmed PO). */
  disabled?: boolean;
  /** Main button: disabled by the renderer when there are no buffered mods. */
  requiresUserMods?: boolean;
  /** Wrap the button in the export-error / ill popover (retry / heal CTAs). */
  popover?: "failed" | "ill";
  /** Render a spinner (confirming pill). */
  spin?: boolean;
  /** Pulse a colored ring to draw attention (modify-mode resolve actions). */
  pulse?: boolean;
}

export interface ResolvedButtons {
  /** Destructive / reject cluster (far-left slot). */
  left: ButtonSpec[];
  /** Primary status-change cluster (right slot). */
  main: ButtonSpec[];
  /**
   * Local chips in the flows-table header — never an ERP export.
   * "Cancel modifications" + (for non-confirmed rows) either "Save" (persist
   * edits, no ERP) or "Accept AI" (resolve pending AI, stay PENDING — the
   * one status change here is the local AI_PROPOSAL → PENDING promotion).
   * Rendered by `RecapModifyCancelSlot`.
   */
  headerSlot: ButtonSpec[];
}

const EMPTY: ResolvedButtons = { left: [], main: [], headerSlot: [] };

// --- descriptor builders (one per concrete button) -------------------------

const reject = (): ButtonSpec => ({
  id: "reject",
  testId: "recap-ai-decline",
  labelKey: "ordersRecap.detailActions.reject",
  fallbackLabel: "Reject",
  tone: "destructive",
  transition: "rejectAi",
});

const rejectAiMods = (): ButtonSpec => ({
  id: "reject-ai",
  testId: "recap-ai-decline",
  labelKey: "ordersRecap.detailActions.rejectAi",
  fallbackLabel: "Reject AI modifications",
  tone: "ai-outline",
  transition: "rejectAi",
});

const rejectAiProposal = (): ButtonSpec => ({
  id: "reject-ai-proposal",
  testId: "recap-ai-decline",
  labelKey: "ordersRecap.detailActions.rejectAiProposal",
  fallbackLabel: "Reject AI proposal",
  tone: "ai-outline",
  transition: "rejectAi",
});

const deleteOrder = (): ButtonSpec => ({
  id: "delete",
  testId: "recap-focus-delete",
  labelKey: "ordersRecap.detailActions.delete",
  fallbackLabel: "Delete",
  tone: "destructive",
  transition: "deleteOrder",
});

const cancelMods = (): ButtonSpec => ({
  id: "cancel-modifications",
  testId: "recap-modify-cancel",
  labelKey: "ordersRecap.detailActions.cancelModifications",
  fallbackLabel: "Cancel modifications",
  tone: "cancel",
  transition: "cancelModifications",
});

/**
 * Table-header "Accept AI" — resolve every pending AI signal and keep the
 * order PENDING (no ERP export), for users whose ERP order type forbids
 * touching an order once entered. It occupies the Save chip's spot and the
 * two never show together: once buffered edits exist, Save takes the slot
 * (its `flushSave` accepts AI alongside the edits anyway).
 */
const acceptAiBtn = (): ButtonSpec => ({
  id: "accept-ai-pending",
  testId: "recap-ai-accept-pending",
  labelKey: "ordersRecap.detailActions.acceptAi",
  fallbackLabel: "Accept AI",
  tone: "ai",
  transition: "acceptAiPending",
});

/** Table-header "Save" — persist local edits WITHOUT a status/ERP change. */
const saveModificationsBtn = (): ButtonSpec => ({
  id: "save-modifications",
  testId: "recap-modify-save-only",
  labelKey: "ordersRecap.detailActions.save",
  fallbackLabel: "Validate modifications",
  tone: "validate",
  transition: "saveModifications",
  requiresUserMods: true,
  pulse: true,
});

/**
 * The single primary action that commits the order and sends it to ERP. Its
 * label + color adapt to WHAT is being committed:
 *  - user modifications present → "Valider et exporter" (orange)
 *  - else a pending AI proposal  → "Accepter et exporter" (violet)
 *  - else (nothing staged)       → "Exporter" (blue)
 * All three run the same `acceptAndConfirm` transition (validate/accept, then
 * trigger the export).
 */
const mainConfirmButton = (
  hasUserMods: boolean,
  hasAi: boolean,
  noExport: boolean,
): ButtonSpec => {
  if (hasUserMods) {
    return {
      id: "validate-confirm",
      testId: "recap-confirm-export-button",
      labelKey: noExport
        ? "ordersRecap.detailActions.validateAndConfirmNoExport"
        : "ordersRecap.detailActions.validateAndConfirm",
      fallbackLabel: noExport ? "Validate and confirm" : "Validate and export",
      tone: "primary",
      transition: "acceptAndConfirm",
    };
  }
  if (hasAi) {
    return {
      id: "accept-confirm",
      testId: "recap-ai-accept",
      labelKey: noExport
        ? "ordersRecap.detailActions.acceptAndConfirmNoExport"
        : "ordersRecap.detailActions.acceptAndConfirm",
      fallbackLabel: noExport ? "Accept and confirm" : "Accept and export",
      tone: "ai",
      transition: "acceptAndConfirm",
    };
  }
  return {
    id: "confirm",
    testId: "recap-confirm-export-button",
    dataState: "draft",
    labelKey: noExport
      ? "ordersRecap.detailActions.confirmNoExport"
      : "ordersRecap.detailActions.confirm",
    fallbackLabel: noExport ? "Confirm" : "Export",
    tone: "confirm",
    transition: "acceptAndConfirm",
  };
};

const unconfirm = (): ButtonSpec => ({
  id: "unconfirm",
  testId: "recap-unconfirm",
  labelKey: "ordersRecap.detailActions.unconfirm",
  fallbackLabel: "Unconfirm",
  tone: "neutral",
  transition: "unconfirm",
});

/** Disabled "Confirm" shown beside Unconfirm on a confirmed PO (flag OFF). */
const confirmDisabled = (noExport: boolean): ButtonSpec => ({
  id: "confirm-disabled",
  testId: "recap-confirm-export-button",
  dataState: "confirmed",
  labelKey: noExport
    ? "ordersRecap.detailActions.confirmNoExport"
    : "ordersRecap.detailActions.confirm",
  fallbackLabel: noExport ? "Confirm" : "Export",
  tone: "confirmed",
  disabled: true,
});

const restore = (): ButtonSpec => ({
  id: "restore",
  testId: "recap-focus-restore",
  labelKey: "ordersRecap.restore",
  fallbackLabel: "Restore",
  tone: "neutral",
  transition: "restore",
});

const confirmingPill = (): ButtonSpec => ({
  id: "confirming",
  testId: "recap-confirm-export-button",
  dataState: "confirming",
  labelKey: "ordersRecap.confirming",
  fallbackLabel: "Confirming…",
  tone: "confirming",
  spin: true,
});

const failedPill = (): ButtonSpec => ({
  id: "failed",
  testId: "recap-confirm-export-button",
  dataState: "failed",
  labelKey: "ordersRecap.status.failed",
  fallbackLabel: "Failed",
  tone: "failed",
  transition: "retryExport",
  popover: "failed",
});

const illPill = (): ButtonSpec => ({
  id: "ill",
  testId: "recap-confirm-export-button",
  dataState: "ill",
  labelKey: "ordersRecap.status.ill",
  fallbackLabel: "Incohérence ERP",
  tone: "ill",
  transition: "healIll",
  popover: "ill",
});

const confirmedPill = (): ButtonSpec => ({
  id: "confirmed",
  testId: "recap-confirm-export-button",
  dataState: "confirmed",
  labelKey: "ordersRecap.status.confirmed",
  fallbackLabel: "Confirmed",
  tone: "confirmed",
});

/**
 * CONFIRMED + `recap_modify_mode` ON, idle: the Edit entry that unlocks the
 * order's cells for the modify-confirmed re-export flow. Entering modify mode
 * is the user's explicit gesture before re-exporting to ERP (a confirmed row
 * is read-only until then). Carries the violet-hint tone when AI changes are
 * pending — see `mainConfirmButton`/CLAUDE.md "Accept AI requires entering
 * modify mode (… the Edit button shows a violet Sparkles hint)".
 */
const editConfirmed = (hasAi: boolean): ButtonSpec => ({
  id: "edit-confirmed",
  testId: "recap-modify-enter",
  labelKey: "ordersRecap.detailActions.modify",
  fallbackLabel: "Modify",
  tone: hasAi ? "ai-outline" : "neutral",
  transition: "enterModify",
});

const exportedPill = (): ButtonSpec => ({
  id: "exported",
  testId: "recap-confirm-export-button",
  dataState: "exported",
  labelKey: "ordersRecap.status.exported",
  fallbackLabel: "Exported",
  tone: "exported",
});

// --- the table -------------------------------------------------------------

/**
 * Resolve the visible buttons for a classified state.
 *
 * @param state          axes from `classifyRecapState` (with `exportPhase`
 *                       already bumped to `confirming` by the renderer when the
 *                       local optimistic-confirm store is set).
 * @param modifyModeFlag value of the `recap_modify_mode` feature flag.
 */
export function resolveButtons(
  state: RecapActionState,
  modifyModeFlag: boolean,
  noExport = false,
): ResolvedButtons {
  const {
    lifecycle,
    status,
    hasPendingAi,
    hasUserMods,
    inModifyMode,
    exportPhase,
  } = state;

  // Table-header local-modification chips: Cancel + (non-confirmed) Save. Save
  // persists edits without an ERP/status change; a CONFIRMED row can't save
  // without re-exporting (it would diverge from M3), so it gets Cancel only.
  // On a non-confirmed row with pending AI and no buffered edits yet, the
  // Save slot instead carries the one-click Accept AI chip (accept + stay
  // PENDING); the first buffered edit swaps it back to Save, whose flushSave
  // accepts AI together with the edits.
  const localHeader = (canSave: boolean): ButtonSpec[] => {
    if (!canSave) return [cancelMods()];
    if (hasPendingAi && !hasUserMods) return [cancelMods(), acceptAiBtn()];
    return [cancelMods(), saveModificationsBtn()];
  };

  // Outside modify mode the header slot is empty except for Accept AI on a
  // pending-AI row — reachable without any editing gesture.
  const idleHeader = (): ButtonSpec[] => (hasPendingAi ? [acceptAiBtn()] : []);

  // Soft-deleted / cancelled → Restore (the only lifecycle action).
  if (lifecycle === "cancelled")
    return { left: [], main: [restore()], headerSlot: [] };

  // Legacy proposal-batch virtual PO — resolved through its own surface; the
  // detail action bar renders nothing for it.
  if (lifecycle === "proposal") return EMPTY;

  // Export in progress overrides everything: one disabled spinner pill. The
  // table is locked behind it; no left/header affordances.
  if (exportPhase === "confirming")
    return { left: [], main: [confirmingPill()], headerSlot: [] };

  // Outside modify mode, an export failure / drift takes precedence over the
  // idle case buttons (matches the legacy failed > ill priority). Inside
  // modify mode the user is mid-edit, so these defer until they save.
  if (!inModifyMode && exportPhase === "failed") {
    const left = status === PreOrderStatus.PENDING ? [deleteOrder()] : [];
    return { left, main: [failedPill()], headerSlot: [] };
  }
  if (!inModifyMode && exportPhase === "ill") {
    return { left: [], main: [illPill()], headerSlot: [] };
  }

  // EXPORTED (status 2): locked, read-only green pill, no actions.
  if (lifecycle === "exported")
    return { left: [], main: [exportedPill()], headerSlot: [] };

  // --- the live action cases (exportPhase idle) ----------------------------
  //
  // The primary action is ONE adaptive button (`mainConfirmButton`):
  //   user mods → "Valider et confirmer" · AI → "Accepter et confirmer" ·
  //   neither → "Confirmer". Only the left (reject / delete) cluster and the
  //   table-header chips differ per state. The table-header Save chip is offered
  //   on non-confirmed rows only (a confirmed row must re-export to stay in
  //   sync with M3, so it gets Cancel only).
  const main = [mainConfirmButton(hasUserMods, hasPendingAi, noExport)];

  if (status === PreOrderStatus.AI_PROPOSAL) {
    return {
      left: [reject()],
      headerSlot: inModifyMode ? localHeader(true) : idleHeader(),
      main,
    };
  }

  if (status === PreOrderStatus.PENDING) {
    const left = hasPendingAi ? [rejectAiMods()] : [deleteOrder()];
    return {
      left,
      headerSlot: inModifyMode ? localHeader(true) : idleHeader(),
      main,
    };
  }

  // CONFIRMED (status 1)
  // Flag OFF: confirmed orders are locked in place. Show a disabled Confirm
  // pill + a dedicated Unconfirm (→ pending → edit → re-Confirm re-exports).
  // Editing in place is impossible when the flag is off, so only the
  // non-modify branch applies; AI keeps an inline Reject (decline needs no
  // editability), Accept-AI is reached via Unconfirm → case 5.
  if (!modifyModeFlag && !inModifyMode) {
    const left = hasPendingAi ? [rejectAiProposal()] : [];
    return {
      left,
      main: [confirmDisabled(noExport), unconfirm()],
      headerSlot: [],
    };
  }

  // Confirmed, no AI, not editing (flag ON) → read-only confirmed pill plus the
  // Edit entry that unlocks the modify-confirmed re-export flow. Entering modify
  // mode is the user's explicit gesture before re-exporting to ERP.
  if (!hasPendingAi && !inModifyMode) {
    return {
      left: [],
      main: [confirmedPill(), editConfirmed(false)],
      headerSlot: [],
    };
  }

  return {
    left: hasPendingAi ? [rejectAiProposal()] : [],
    headerSlot: inModifyMode ? localHeader(false) : [],
    main,
  };
}
